import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { User } from "@supabase/supabase-js";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { authService, profileService } from "@/lib/services";
import { isSafari } from "@/lib/utils/browser-detection";
import { logAuthError, createLogContext } from "@/lib/utils/logger";
import { getSupabaseErrorMessage, isSessionExpiredError, isNetworkError } from "@/lib/utils/supabase-error-messages";
import { getLocalizedAuthErrorMessage } from "@/lib/utils/auth-error-message";
import i18n from "@/lib/i18n/i18n";
import { identifyUserFromSupabase, resetUser, trackEvent } from "@/lib/services/posthog.service";
import type { HeardFromValue } from "@/constants/heard-from";

export type DocumentType =
  | "driver_license"
  | "identity_card"
  | "bank_details"
  | "vehicle_registration"
  | "insurance";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (data: {
    email: string;
    password: string;
    options: {
      data: {
        first_name?: string;
        last_name?: string;
        agency_name?: string; // Pour les agences (role = 'owner')
        role: "owner" | "renter";
        terms_version?: string;
        terms_accepted_at?: string;
        heard_from?: HeardFromValue;
        phone?: string;
        city?: string;
        fleet_size?: string;
      };
      /** Path to resume after email verification. Not stored as user metadata. */
      redirectTo?: string | null;
    };
  }) => Promise<boolean>;
  signOut: () => Promise<void>;
  updateProfile: (data: {
    first_name?: string;
    last_name?: string;
    phone?: string;
  }) => Promise<void>;
  getUserRole: () => Promise<"owner" | "renter" | "admin" | null>;
  refreshUser: () => Promise<void>;
  isVerifiedTenant: () => Promise<boolean>;
  isVerifiedHost: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const initializedRef = useRef(false);
  const subscriptionRef = useRef<
    ReturnType<typeof supabase.auth.onAuthStateChange>["data"]["subscription"] | null
  >(null);

  const resolveUserRole = useCallback(
    async (targetUser: User | null): Promise<"owner" | "renter" | "admin" | null> => {
      if (!targetUser) return null;

      try {
        const { roleService } = await import("@/lib/services/role.service");
        const role = await roleService.getUserRole(targetUser.id);

        if (role) {
          return role;
        }

        const metadataRole = targetUser.user_metadata?.role as
          | "owner"
          | "renter"
          | "admin"
          | "host"
          | undefined;
        return metadataRole === "host" ? "owner" : metadataRole || null;
      } catch (err) {
        logAuthError(
          "Error getting user role",
          createLogContext(targetUser.id, "resolveUserRole", "AuthContext"),
          err instanceof Error ? err : undefined
        );
        const metadataRole = targetUser.user_metadata?.role as
          | "owner"
          | "renter"
          | "admin"
          | "host"
          | undefined;
        return metadataRole === "host" ? "owner" : metadataRole || null;
      }
    },
    []
  );

  // Initialize: Get session on mount (only once)
  useEffect(() => {
    // Prevent re-initialization
    if (initializedRef.current) return;
    initializedRef.current = true;

    let isInitialized = false;

    // Fetch session FIRST to avoid flash
    // Phase 2.1 : Utilise retrySessionOperation pour centraliser la logique de retry
    const initializeAuth = async () => {
      try {
        // Utiliser retrySessionOperation qui gère automatiquement les retries pour Safari
        const session = await authService.getSession();

        if (session?.user) {
          setUser(session.user);
          
          // Identifier l'utilisateur dans PostHog si déjà connecté
          resolveUserRole(session.user).then((role) => {
            identifyUserFromSupabase(session.user, role);
          }).catch(() => {
            // Si erreur, identifier quand même avec les métadonnées
            identifyUserFromSupabase(session.user);
          });
        }
        isInitialized = true;
        setIsLoading(false);
      } catch (err) {
        // Phase 3.3 : Utiliser le système de logging centralisé
        logAuthError(
          "Error initializing auth",
          createLogContext(undefined, "initializeAuth", "AuthContext"),
          err instanceof Error ? err : undefined
        );
        isInitialized = true;
        setIsLoading(false);
      }
    };

    // Start fetching session immediately
    initializeAuth();

    // Set up auth state listener for future changes and OAuth callback
    // INITIAL_SESSION = session from storage/URL at load ; SIGNED_IN = after login/exchangeCodeForSession
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (import.meta.env.DEV) {
        console.log("[AuthContext] onAuthStateChange:", event, session?.user?.id ?? "no user");
      }
      if (isInitialized) {
        if (event === "INITIAL_SESSION" || event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
          setUser(session?.user ?? null);
          setIsLoading(false);
          setError(null);
        } else if (event === "SIGNED_OUT") {
          setUser(null);
          setIsLoading(false);
          setError(null);
        } else if (event === "USER_UPDATED") {
          setUser(session?.user ?? null);
        }
      } else {
        setUser(session?.user ?? null);
        setIsLoading(false);
        isInitialized = true;
      }
    });

    subscriptionRef.current = subscription;

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [resolveUserRole]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      let timeoutId: NodeJS.Timeout | null = null;

      try {
        setError(null);
        setIsLoading(true);

        // Safety timeout for Safari (30 seconds)
        if (isSafari()) {
          timeoutId = setTimeout(() => {
            setIsLoading(false);
            setError("La connexion a pris trop de temps. Veuillez réessayer.");
          }, 30000);
        }

        const { user: authUser } = await authService.signIn({ email, password });

        // Clear timeout if successful
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        if (authUser) {
          setUser(authUser);
          
          // Identifier l'utilisateur dans PostHog
          resolveUserRole(authUser).then((role) => {
            identifyUserFromSupabase(authUser, role, {
              is_login: true,
              login_method: "email",
            });
          }).catch(() => {
            // Si erreur, identifier quand même avec les métadonnées
            identifyUserFromSupabase(authUser, undefined, {
              is_login: true,
              login_method: "email",
            });
          });
          
          toast({
            title: "Connexion réussie",
            description: "Bienvenue !",
          });
        }
      } catch (err) {
        // Clear timeout on error
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        // Vérifier si c'est une erreur de session expirée
        if (isSessionExpiredError(err)) {
          // Tenter de rafraîchir la session
          try {
            const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError || !refreshedSession) {
              // Refresh échoué, déconnexion propre
              await authService.signOut();
              setUser(null);
              toast({
                variant: "destructive",
                title: "Session expirée",
                description: "Votre session a expiré. Veuillez vous reconnecter.",
              });
              throw new Error("Session expirée");
            } else {
              // Refresh réussi, continuer avec la nouvelle session
              setUser(refreshedSession.user);
              return;
            }
          } catch (refreshErr) {
            // Erreur lors du refresh, déconnexion propre
            await authService.signOut();
            setUser(null);
            toast({
              variant: "destructive",
              title: "Session expirée",
              description: "Votre session a expiré. Veuillez vous reconnecter.",
            });
            throw refreshErr;
          }
        }

        // Email non vérifié : la page de connexion affiche le message dédié
        // et redirige vers la vérification. Ne pas afficher de toast générique ici.
        if (err instanceof Error && err.message === "EMAIL_NOT_VERIFIED") {
          setError(i18n.t("errors.auth.emailNotVerified"));
          throw err;
        }

        // Message traduit dans la langue active (les services lancent des clés i18n)
        const errorMessage = getLocalizedAuthErrorMessage(err, "errors.auth.loginFailed");
        setError(errorMessage);
        toast({
          variant: "destructive",
          title: i18n.t("auth.login.errors.connectionErrorTitle"),
          description: errorMessage,
        });
        throw err;
      } finally {
        // Clear timeout in finally
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        setIsLoading(false);
      }
    },
    [toast, resolveUserRole]
  );

  const signUp = useCallback(
    async (data: {
      email: string;
      password: string;
      options: {
        data: {
          first_name?: string;
          last_name?: string;
          agency_name?: string; // Pour les agences (role = 'owner')
          role: "owner" | "renter";
          terms_version?: string;
          terms_accepted_at?: string;
          heard_from?: HeardFromValue;
          phone?: string;
          city?: string;
          fleet_size?: string;
        };
        /** Path to resume after email verification. Not stored as user metadata. */
        redirectTo?: string | null;
      };
    }) => {
      try {
        setError(null);
        setIsLoading(true);

        const { user: authUser } = await authService.signUp({
          email: data.email,
          password: data.password,
          first_name: data.options.data.first_name,
          last_name: data.options.data.last_name,
          agency_name: data.options.data.agency_name,
          role: data.options.data.role,
          terms_version: data.options.data.terms_version,
          terms_accepted_at: data.options.data.terms_accepted_at,
          heard_from: data.options.data.heard_from,
          phone: data.options.data.phone,
          city: data.options.data.city,
          fleet_size: data.options.data.fleet_size,
          redirectTo: data.options.redirectTo,
        });

        if (authUser) {
          // Ne pas setUser ici - l'email n'est pas encore vérifié
          // L'utilisateur aura une vraie session après avoir cliqué sur le lien de vérification
          // Ne pas afficher de toast - Register.tsx affiche son propre toast plus informatif
          
          // Track l'événement signup dans PostHog (sans PII email — rôle + canal d'acquisition)
          trackEvent("sign_up", {
            role: data.options.data.role,
            method: "email",
            heard_from: data.options.data.heard_from || "not_provided",
            platform: "web",
          });
          
          return true;
        }

        return false;
      } catch (err) {
        // Message traduit dans la langue active (les services lancent des clés i18n)
        const errorMessage = getLocalizedAuthErrorMessage(err, "errors.auth.signupFailed");
        setError(errorMessage);
        toast({
          variant: "destructive",
          title: "Erreur d'inscription",
          description: errorMessage,
        });
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [toast]
  );

  const signOut = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);

      // Reset PostHog user tracking
      resetUser();

      await authService.signOut();
      setUser(null);
      toast({
        title: "Déconnexion réussie",
        description: "À bientôt !",
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to sign out";
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const refreshUser = useCallback(async () => {
    try {
      const authUser = await authService.getCurrentUser();
      if (authUser) {
        setUser(authUser);
      }
    } catch (err) {
      // Gérer les erreurs de session expirée
      if (isSessionExpiredError(err)) {
        // Session expirée, nettoyer l'état utilisateur
        setUser(null);
        logAuthError(
          "Session expired during refresh",
          createLogContext(user?.id, "refreshUser", "AuthContext"),
          err instanceof Error ? err : undefined
        );
        return;
      }

      // Gérer les erreurs réseau avec retry
      if (isNetworkError(err)) {
        // Pour les erreurs réseau, on ne met pas à jour l'utilisateur mais on log
        logAuthError(
          "Network error refreshing user",
          createLogContext(user?.id, "refreshUser", "AuthContext"),
          err instanceof Error ? err : undefined
        );
        return;
      }

      // Autres erreurs : logger mais ne pas changer l'état utilisateur
      logAuthError(
        "Error refreshing user",
        createLogContext(user?.id, "refreshUser", "AuthContext"),
        err instanceof Error ? err : undefined
      );
    }
  }, [user]);

  const updateProfile = useCallback(
    async (data: { first_name?: string; last_name?: string; phone?: string }) => {
      if (!user) throw new Error("No user logged in");
      try {
        setError(null);
        setIsLoading(true);

        await profileService.updateProfile(data);
        toast({
          title: "Profil mis à jour",
          description: "Vos informations ont été mises à jour avec succès.",
        });
      } catch (err) {
        // Utiliser le message d'erreur traduit
        const errorMessage = getSupabaseErrorMessage(err, "Erreur lors de la mise à jour du profil");
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [user, toast]
  );

  /**
   * Get user role
   * Optimized: Uses roleService with caching for better performance
   */
  const getUserRole = useCallback(async (): Promise<"owner" | "renter" | "admin" | null> => {
    if (user) {
      return resolveUserRole(user);
    }

    try {
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();
      return resolveUserRole(currentUser);
    } catch {
      return null;
    }
  }, [resolveUserRole, user]);

  const isVerifiedTenant = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    try {
      return await profileService.isVerifiedTenant(user.id);
    } catch (err) {
      // Phase 3.3 : Utiliser le système de logging centralisé
      logAuthError(
        "Error checking verified_tenant",
        createLogContext(user.id, "isVerifiedTenant", "AuthContext"),
        err instanceof Error ? err : undefined
      );
      return false;
    }
  }, [user]);

  const isVerifiedHost = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    try {
      return await profileService.isVerifiedHost(user.id);
    } catch (err) {
      // Phase 3.3 : Utiliser le système de logging centralisé
      logAuthError(
        "Error checking verified_host",
        createLogContext(user.id, "isVerifiedHost", "AuthContext"),
        err instanceof Error ? err : undefined
      );
      return false;
    }
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      isLoading,
      error,
      signIn,
      signUp,
      signOut,
      updateProfile,
      getUserRole,
      refreshUser,
      isVerifiedTenant,
      isVerifiedHost,
    }),
    [
      user,
      isLoading,
      error,
      signIn,
      signUp,
      signOut,
      updateProfile,
      getUserRole,
      refreshUser,
      isVerifiedTenant,
      isVerifiedHost,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

/**
 * Optional auth hook for components that may render outside AuthProvider (e.g. Navbar in some layouts).
 * Returns undefined when outside provider; callers should treat as "no user".
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useOptionalAuth = (): AuthContextType | undefined => {
  return useContext(AuthContext);
};
